// src/screens/customer/auth/ResetPassword.jsx

import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/customer/forgetPassword/ForgotPassword.css";

export default function ResetPassword() {
  const { baseUrl, RegExpChecker } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const otp = location.state?.otp;

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If someone lands here directly without verifying OTP first, send them back
  useEffect(() => {
    if (!email || !otp) {
      navigate("/forgot-password");
    }
  }, [email, otp, navigate]);

  const handleReset = () => {
    const passwordErr = RegExpChecker("password", password);
    const errs = { ...passwordErr };

    if (password !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match.";
    }

    const hasErrors = Object.values(errs).some((msg) => msg !== "");
    if (hasErrors) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("otp", otp);
    formData.append("password", password);

    axios
      .post(`${baseUrl}/customer/forgot-password/resetPassword`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setIsSubmitting(false);
        if (response.data.success) {
          navigate("/", { state: { passwordResetSuccess: true } });
        } else {
          setErrors({
            general: response.data.message || "Something went wrong.",
          });
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        setErrors({
          general:
            error.response?.data?.message ||
            "Something went wrong. Please try again.",
        });
      });
  };

  return (
    <div className="fp-page">
      <div className="fp-card">
        <button
          className="fp-back-arrow"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="fp-header">
          <h2 className="fp-title">Reset password</h2>
          <p className="fp-subtitle">Choose a new password for your account</p>
        </div>

        <div className="fp-form-body">
          {errors.general && (
            <div className="fp-general-error">⚠ {errors.general}</div>
          )}

          <div className="fp-field">
            <label className="fp-label" htmlFor="fp-password">
              New Password
            </label>
            <div className="fp-input-wrap">
              <span className="fp-input-icon">
                <Lock size={16} />
              </span>
              <input
                id="fp-password"
                type={showPw ? "text" : "password"}
                className={`fp-input${errors.password ? " fp-input-error" : ""}`}
                placeholder="Your new password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((p) => ({
                    ...p,
                    ...RegExpChecker("password", e.target.value),
                  }));
                }}
                autoComplete="new-password"
              />
              <button
                className="fp-eye-btn"
                type="button"
                onClick={() => setShowPw((p) => !p)}
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            </div>
            {errors.password && (
              <span className="fp-error-msg">⚠ {errors.password}</span>
            )}
          </div>

          <div className="fp-field">
            <label className="fp-label" htmlFor="fp-confirm">
              Confirm Password
            </label>
            <div className="fp-input-wrap">
              <span className="fp-input-icon">
                <Lock size={16} />
              </span>
              <input
                id="fp-confirm"
                type={showPw ? "text" : "password"}
                className={`fp-input${errors.confirmPassword ? " fp-input-error" : ""}`}
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword)
                    setErrors((p) => ({ ...p, confirmPassword: "" }));
                }}
                autoComplete="new-password"
              />
            </div>
            {errors.confirmPassword && (
              <span className="fp-error-msg">⚠ {errors.confirmPassword}</span>
            )}
          </div>

          <button
            className="fp-submit-btn"
            type="button"
            onClick={handleReset}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Resetting…" : "Reset Password"}
          </button>
        </div>
      </div>
    </div>
  );
}
